import { MessageSquare } from 'lucide-react';
import { Card } from '../../../shared/components/ui/card';
import { ConversationList } from './ConversationList';
import { ChatInterface } from './ChatInterface';
import { useWebSocketConnection } from '../hooks/useMessages';
import { useMessageLinkRecipient } from '../hooks/useMessageLinkRecipient';
import { Conversation } from '../types';
import { PermissionGate } from '@/features/workspace-sub-delegation';

/**
 * Main Messages Page Component
 * 
 * Implements FR-077 through FR-081:
 * - Two-column layout: conversations list + chat interface
 * - Real-time message delivery via WebSocket
 * - Typing indicators and read receipts
 * - Mobile-responsive (stacks on small screens)
 * 
 * Layout:
 * - Left panel: Conversation list (42% width on desktop)
 * - Right panel: Chat interface (58% width on desktop)
 * - Mobile: Single column, toggle between list and chat
 */
/**
 * Inner content — mounted only once the MESSAGING permission gate passes, so
 * a delegate without `MESSAGING` never fires the conversations fetch or opens
 * the WebSocket connection.
 */
const MessagesPageContent = () => {
  // Establish WebSocket connection (only once)
  useWebSocketConnection();

  // Resolves the serviceProviderId / recipientUserId (or legacy `recipient`)
  // URL parameter into a conversation. See useMessageLinkRecipient for the
  // full recipient-resolution + ambiguous-link handling.
  const {
    selectedConversation,
    setSelectedConversation,
    showChat,
    setShowChat,
    isLoadingRecipient,
    linkError,
    getProfileViewEngagementId,
    clearProfileViewCorrelation,
    isNewConversation,
    setIsNewConversation,
  } = useMessageLinkRecipient();

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    // Selecting from the server-sourced list is always an existing
    // conversation — never carry over a stale "confirmed new" signal from a
    // previous deep-link resolution (spec ticket 10 §6.4).
    setIsNewConversation(false);
    setShowChat(true);
  };

  const handleBackToList = () => {
    setShowChat(false);
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-background">
      <div className="container mx-auto px-4 pt-8">
        <div className="mb-8">
          <h1 className="text-3xl text-gray-900 mb-2">Messages</h1>
          <p className="text-gray-600">Connect with providers and advisors</p>
        </div>
        {linkError && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {linkError}
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden">
        <div className="container mx-auto px-4 h-full">
          {/* Desktop Layout: Two-column */}
          <div className="hidden lg:grid lg:grid-cols-12 h-full gap-4 p-4">
            {/* Conversations List - Left Panel */}
            <Card className="col-span-5 overflow-hidden min-w-[320px]">
              <ConversationList
                selectedConversationId={selectedConversation?.conversationId}
                onSelectConversation={handleSelectConversation}
              />
            </Card>

            {/* Chat Interface - Right Panel */}
            <Card className="col-span-7 overflow-hidden">
              {isLoadingRecipient ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <MessageSquare className="h-16 w-16 text-gray-300 mb-4 animate-pulse" />
                  <h2 className="text-xl font-semibold text-gray-700 mb-2">
                    Starting conversation...
                  </h2>
                  <p className="text-gray-500">
                    Please wait while we prepare your chat
                  </p>
                </div>
              ) : selectedConversation ? (
                <ChatInterface
                  conversation={selectedConversation}
                  profileViewEngagementId={getProfileViewEngagementId(selectedConversation.participant.id)}
                  onProfileViewConsumed={clearProfileViewCorrelation}
                  isNewConversation={isNewConversation}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <MessageSquare className="h-16 w-16 text-gray-300 mb-4" />
                  <h2 className="text-xl font-semibold text-gray-700 mb-2">
                    Select a conversation
                  </h2>
                  <p className="text-gray-500">
                    Choose a conversation from the list to start messaging
                  </p>
                </div>
              )}
            </Card>
          </div>

          {/* Mobile Layout: Single column with toggle */}
          <div className="lg:hidden h-full p-4">
            <Card className="h-full overflow-hidden">
              {!showChat ? (
                // Show conversation list
                <ConversationList
                  selectedConversationId={selectedConversation?.conversationId}
                  onSelectConversation={handleSelectConversation}
                />
              ) : (
                // Show chat interface
                <div className="h-full flex flex-col">
                  {/* Back button for mobile */}
                  <div className="flex items-center gap-2 p-3 border-b">
                    <button
                      onClick={handleBackToList}
                      className="text-[#026857] hover:text-[#025247] font-medium text-sm"
                    >
                      ← Back to conversations
                    </button>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    {selectedConversation && (
                      <ChatInterface
                        conversation={selectedConversation}
                        profileViewEngagementId={getProfileViewEngagementId(selectedConversation.participant.id)}
                        onProfileViewConsumed={clearProfileViewCorrelation}
                        isNewConversation={isNewConversation}
                      />
                    )}
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MessagesPage = () => (
  <PermissionGate permission="MESSAGING" feature="Messages" variant="page">
    <MessagesPageContent />
  </PermissionGate>
);

